import { ManagerRole } from '../permissions/PermissionResolver';

/** Who takes over in the change-owner dialog. */
export const NewOwnerChoice = Object.freeze({
  Team: 'team',
  New: 'new',
  None: 'none',
});

/** What happens to the current owner (backend `PreviousOwnerOutcome`). */
export const PreviousOwnerOutcome = Object.freeze({
  KeepAsOperator: 'keep_as_operator',
  Remove: 'remove',
});

/** `error_code`s of PUT /business-managers/owner (backend `OwnerChangeRefusalCause`). */
export const OwnerChangeError = Object.freeze({
  EmailIsOwner: 'email_is_owner',
  EmailIsManager: 'email_is_manager',
  EmailTaken: 'email_taken',
  ManagerNotOnTeam: 'manager_not_on_team',
  OwnerChanged: 'owner_changed',
  NothingToChange: 'nothing_to_change',
  NoLegalEntity: 'no_legal_entity',
  InvalidEmail: 'invalid_email',
});

const EMAIL_ERRORS = [
  OwnerChangeError.EmailIsOwner,
  OwnerChangeError.EmailIsManager,
  OwnerChangeError.EmailTaken,
  OwnerChangeError.InvalidEmail,
];

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const normalizeEmail = (email) => (email ?? '').trim().toLowerCase();

export function isValidEmail(email) {
  return EMAIL.test((email ?? '').trim());
}

/**
 * Everyone who can become the owner: Operators and Employees of the team, never the
 * current owner or another restaurant's owner (the backend refuses them).
 */
export function ownerCandidates(managers, ownerId) {
  return (managers ?? []).filter(
    (manager) =>
      manager.id !== ownerId &&
      !manager.is_owner &&
      !(manager.roles ?? []).includes(ManagerRole.StoreAdmin) &&
      (manager.roles ?? []).some((role) => [ManagerRole.StoreOperator, ManagerRole.StoreEmployee].includes(role)),
  );
}

/** Whether Continue is enabled: both questions answered, and a pick or the three fields for someone new. */
export function isChoiceComplete({ choice, managerId, invite, previous, hasOwner }) {
  if (hasOwner && !previous) {
    return false;
  }

  if (choice === NewOwnerChoice.Team) {
    return Boolean(managerId);
  }

  if (choice === NewOwnerChoice.New) {
    return [invite?.first_name, invite?.last_name, invite?.email].every((value) => (value ?? '').trim() !== '');
  }

  return choice === NewOwnerChoice.None && hasOwner;
}

/**
 * The email check on Continue, before anything is sent: an invalid email, the current
 * owner's, or someone already on the team (who the dialog offers instead).
 * Returns `{ code, manager }` or null.
 */
export function checkNewOwnerEmail(email, { owner, candidates }) {
  if (!isValidEmail(email)) {
    return { code: OwnerChangeError.InvalidEmail, manager: null };
  }

  const wanted = normalizeEmail(email);

  if (owner && normalizeEmail(owner.email) === wanted) {
    return { code: OwnerChangeError.EmailIsOwner, manager: owner };
  }

  const member = (candidates ?? []).find((manager) => normalizeEmail(manager.email) === wanted);

  return member ? { code: OwnerChangeError.EmailIsManager, manager: member } : null;
}

/** PUT /business-managers/owner body. */
export function buildChangeOwnerPayload({ companyId, owner, choice, managerId, invite, previous }) {
  const newOwner = {
    [NewOwnerChoice.Team]: () => ({ manager_id: managerId }),
    [NewOwnerChoice.New]: () => ({
      first_name: invite.first_name.trim(),
      last_name: invite.last_name.trim(),
      email: invite.email.trim(),
    }),
    [NewOwnerChoice.None]: () => null,
  }[choice];

  return {
    company_id: companyId,
    current_owner_id: owner?.id ?? null,
    new_owner: newOwner(),
    previous_owner: owner ? previous : null,
  };
}

/**
 * A refusal from the backend, as the dialog shows it: an email problem goes under
 * the email field (back on step 1), anything else is the alert on the confirm step.
 */
export function readChangeOwnerError(body) {
  const code = body?.error_code ?? (body?.errors?.['new_owner.email'] ? OwnerChangeError.InvalidEmail : null);

  return {
    code,
    isEmailError: EMAIL_ERRORS.includes(code),
    manager: body?.payload?.manager ?? null,
    message: body?.description ?? body?.message ?? null,
  };
}

/** Analytics value of the new owner (`owner_change_confirmed.new_owner`). */
export function newOwnerKind(choice) {
  return { [NewOwnerChoice.Team]: 'existing', [NewOwnerChoice.New]: 'new', [NewOwnerChoice.None]: 'none' }[choice];
}
